<template>
  <el-drawer
    :visible="visible"
    :title="drawerTitle"
    direction="rtl"
    size="70%"
    append-to-body
    @update:visible="handleVisibleChange"
  >
    <div v-loading="loading" class="pos-id-detail px-3 pb-4">
      <b-alert v-if="errorText" variant="danger" show>{{ errorText }}</b-alert>

      <template v-if="detail">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="mb-0">重定向映射詳情</h6>
          <b-button
            size="sm"
            variant="outline-secondary"
            @click="handleVisibleChange(false)"
          >
            關閉
          </b-button>
        </div>

        <b-alert v-if="dead" show variant="danger" class="mb-2">
          新號台帳已不存在，這條映射已是死規則
        </b-alert>
        <b-alert v-if="fromLedgerExists" show variant="warning" class="mb-2">
          舊號台帳仍存在（可能是雙向映射），刪除前請確認哪一邊才是目標
        </b-alert>

        <h6 class="mb-1">基本資訊</h6>
        <b-table
          bordered
          small
          striped
          :items="basicRows"
          :fields="itemFields"
          class="mb-3"
        />

        <h6 class="mb-1">舊號剩餘票房（from_engagements）</h6>
        <b-table
          bordered
          small
          striped
          :items="engagementRows"
          :fields="itemFields"
          class="mb-3"
        />

        <h6 class="mb-1">新號台帳（to_record）</h6>
        <template v-if="toRecord">
          <b-table
            bordered
            small
            striped
            :items="toRecordRows"
            :fields="itemFields"
            class="mb-3"
          />
        </template>
        <p v-else class="text-danger">
          to_record = null：新號台帳已不存在，這條映射已是死規則，可優先清理。
        </p>

        <h6 class="mb-1">最近 5 條日結票房明細（samples）</h6>
        <b-table
          bordered
          small
          striped
          show-empty
          empty-text="該舊號最近沒有日結票房記錄"
          :items="samples"
          :fields="sampleFields"
          class="mb-3"
        >
          <template #cell(cinema_id)="row">
            {{ textOrDash(row.item.cinema_id) }}
          </template>
          <template #cell(show_time)="row">
            <span class="text-nowrap">{{ textOrDash(row.item.show_time) }}</span>
          </template>
          <template #cell(date_of_engagement)="row">
            <span class="text-nowrap">
              {{ textOrDash(row.item.date_of_engagement) }}
            </span>
          </template>
          <template #cell(total_gross)="row">
            {{ moneyText(row.item.total_gross) }}
          </template>
          <template #cell(total_admissions)="row">
            {{ formatNumber(row.item.total_admissions) }}
          </template>
        </b-table>
      </template>

      <p v-else-if="!loading" class="text-muted">沒有可顯示的記錄。</p>
    </div>
  </el-drawer>
</template>

<script>
import { Message } from 'element-ui'
import { getManualFixMapping } from '@/api/posId'
import {
  formatNumber,
  hasFromLedger,
  isDeadMapping,
  mappingEngagementSummary,
  resolvePosIdError
} from '@/utils/posId'

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function textOrDash(value) {
  return hasText(value) ? String(value) : '—'
}

function rangeText(from, to) {
  if (!hasText(from) && !hasText(to)) {
    return '不限（全部日期）'
  }
  const start = hasText(from) ? String(from) : '不限'
  const end = hasText(to) ? String(to) : '不限'
  return `${start} ~ ${end}`
}

/**
 * P6 詳情抽屜（guide §5.2 / §5.4-3 / §5.4-4）：
 * 列表全部欄位 + 舊號剩餘票房 + 新號台帳 + samples[]（該舊號最近 5 條 engagement_finals），
 * 死規則（to_record = null）標紅、舊號台帳仍在（from_ledger_exists）標橘提醒。
 */
export default {
  name: 'MappingDetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    recordId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      errorText: '',
      detail: null,
      itemFields: [
        { key: 'item', label: '項目', thStyle: { width: '250px' } },
        { key: 'value', label: '內容' }
      ],
      sampleFields: [
        { key: 'id', label: '記錄 id', thStyle: { width: '90px' } },
        { key: 'cinema_id', label: '戲院 id', thStyle: { width: '100px' } },
        { key: 'show_time', label: '場次時間', thStyle: { width: '160px' } },
        { key: 'date_of_engagement', label: '日結日期', thStyle: { width: '120px' } },
        { key: 'total_gross', label: '金額', thStyle: { width: '120px' } },
        { key: 'total_admissions', label: '人次', thStyle: { width: '100px' } }
      ]
    }
  },
  computed: {
    drawerTitle() {
      return this.detail ? `重定向映射詳情 #${this.detail.id}` : '重定向映射詳情'
    },
    dead() {
      return isDeadMapping(this.detail)
    },
    fromLedgerExists() {
      return hasFromLedger(this.detail)
    },
    toRecord() {
      const record = this.detail ? this.detail.to_record : null
      return record && typeof record === 'object' ? record : null
    },
    engagementRaw() {
      const engagements = this.detail ? this.detail.from_engagements : null
      return engagements && typeof engagements === 'object' ? engagements : {}
    },
    engagementSummary() {
      return mappingEngagementSummary(this.engagementRaw)
    },
    samples() {
      const list = this.detail ? this.detail.samples : null
      return Array.isArray(list) ? list : []
    },
    basicRows() {
      const row = this.detail
      if (!row) {
        return []
      }
      return [
        { item: '記錄 id', value: textOrDash(row.id) },
        { item: '院線（from_circuit_id）', value: textOrDash(row.from_circuit_id) },
        { item: '舊號（from_pos_id）', value: textOrDash(row.from_pos_id) },
        { item: '新號（to_pos_id）', value: textOrDash(row.to_pos_id) },
        { item: '標題（from_title_name）', value: textOrDash(row.from_title_name) },
        {
          item: '新號台帳是否存在',
          value: this.dead ? '否（死規則）' : '是'
        },
        {
          item: '舊號台帳是否存在（from_ledger_exists）',
          value: this.fromLedgerExists ? '是（可能是雙向映射）' : '否'
        },
        { item: '建立時間', value: textOrDash(row.created_at) },
        { item: '更新時間', value: textOrDash(row.updated_at) }
      ]
    },
    engagementRows() {
      const summary = this.engagementSummary
      return [
        { item: '日結場次（finals_count）', value: formatNumber(summary.finalsCount) },
        { item: '日結金額（finals_gross）', value: this.moneyText(summary.gross) },
        { item: '日結人次（finals_admissions）', value: formatNumber(summary.admissions) },
        {
          item: '每小時場次（hourlies_count）',
          value: formatNumber(this.engagementRaw.hourlies_count)
        },
        { item: '日期範圍', value: rangeText(summary.dateFrom, summary.dateTo) }
      ]
    },
    toRecordRows() {
      const record = this.toRecord
      if (!record) {
        return []
      }
      return [
        { item: '台帳 id', value: textOrDash(record.id) },
        { item: 'pos_id', value: textOrDash(record.pos_id) },
        { item: '影片 id（film_id）', value: textOrDash(record.film_id) },
        { item: 'master id（master_id）', value: textOrDash(record.master_id) },
        { item: '台帳標題（title_name）', value: textOrDash(record.title_name) },
        { item: '狀態（status）', value: hasText(record.status) ? String(record.status) : '有效' }
      ]
    }
  },
  watch: {
    visible(value) {
      if (value === true) {
        this.loadDetail()
      }
    },
    recordId() {
      if (this.visible === true) {
        this.loadDetail()
      }
    }
  },
  methods: {
    formatNumber,
    textOrDash,
    moneyText(value) {
      return `$${formatNumber(value)}`
    },
    rangeText,
    handleVisibleChange(value) {
      this.$emit('update:visible', value === true)
    },
    loadDetail() {
      if (!hasText(this.recordId)) {
        return
      }
      this.loading = true
      this.errorText = ''
      getManualFixMapping(this.recordId)
        .then((res) => {
          this.detail = res && res.data ? res.data : null
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.detail = null
          const info = resolvePosIdError(error)
          this.errorText = info.message
          Message.error(info.message)
          if (info.isPermissionDenied) {
            this.$emit('read-only')
            this.$emit('update:visible', false)
          }
          if (info.code === 10010) {
            this.$emit('missing')
          }
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.pos-id-detail {
  h6 {
    color: #0e72bc;
    font-weight: 500;
  }
  ::v-deep table {
    table-layout: fixed;
  }
  ::v-deep td,
  ::v-deep th {
    white-space: normal;
    word-break: break-word;
    vertical-align: top;
  }
}
</style>
